import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Chip, Typography, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField, Button, Paper } from "@mui/material";
import BookmarkIcon from "@mui/icons-material/BookmarkBorder";
import AddIcon from "@mui/icons-material/Add";
import styled from "@emotion/styled";
import { setUserPreferences } from "../redux/slices/auth";
import { updateUser } from "../redux/slices/users";
import { showAlert } from "../redux/slices/alerts";

const PresetsPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(1.5, 2),
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
}));

const PresetsLabel = styled(Typography)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(0.5),
}));

const EmptyHint = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontStyle: "italic",
}));

const AddChip = styled(Chip)({
    marginLeft: "auto",
});

const MasterReportPresets = ({ filters, sorting, onApply }) => {
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.auth.currentUser);
    const userId = useSelector((state) => state.auth.userId);
    const presets = currentUser?.preferences?.masterReport?.presets || [];

    const [saveOpen, setSaveOpen] = useState(false);
    const [name, setName] = useState("");
    const [pendingDelete, setPendingDelete] = useState(null);

    const persist = (nextPresets) => {
        if (!userId) return;
        const preferences = {
            ...(currentUser?.preferences || {}),
            masterReport: {
                ...(currentUser?.preferences?.masterReport || {}),
                presets: nextPresets,
            },
        };
        dispatch(setUserPreferences({ preferences }));
        dispatch(updateUser({ userId, userData: { preferences }, silent: true }));
    };

    const handleOpenSave = () => {
        setName("");
        setSaveOpen(true);
    };

    const handleSave = () => {
        const trimmed = name.trim();
        if (!trimmed) return;
        const preset = { name: trimmed, filters, sorting };
        const existingIndex = presets.findIndex((item) => item.name.toLowerCase() === trimmed.toLowerCase());
        const nextPresets = existingIndex >= 0
            ? presets.map((item, index) => (index === existingIndex ? preset : item))
            : [...presets, preset];
        persist(nextPresets);
        setSaveOpen(false);
        dispatch(showAlert({ severity: "success", message: `Saved report "${trimmed}".` }));
    };

    const handleDelete = () => {
        persist(presets.filter((item) => item.name !== pendingDelete.name));
        setPendingDelete(null);
    };

    const duplicateName = presets.some((item) => item.name.toLowerCase() === name.trim().toLowerCase());

    return (
        <>
            <PresetsPaper elevation={2}>
                <PresetsLabel variant="subtitle2">
                    <BookmarkIcon fontSize="small" />
                    Saved reports
                </PresetsLabel>
                {presets.length === 0 ? (
                    <EmptyHint variant="body2">None yet — set up your filters and sorting, then save them here to reuse in one click.</EmptyHint>
                ) : (
                    presets.map((preset) => (
                        <Tooltip key={preset.name} title="Apply these saved filters and sorting, and run the report">
                            <Chip label={preset.name} color="primary" variant="outlined" onClick={() => onApply(preset)} onDelete={() => setPendingDelete(preset)} />
                        </Tooltip>
                    ))
                )}
                <Tooltip title="Save the filters and sorting you have right now as a reusable report">
                    <AddChip icon={<AddIcon />} label="Save current view" onClick={handleOpenSave} />
                </Tooltip>
            </PresetsPaper>

            <Dialog open={saveOpen} onClose={() => setSaveOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Save this view as a report</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Your current filters and sorting will be stored under this name. Click it any time to run the same report again.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        margin="normal"
                        label="Report name"
                        placeholder="e.g. My overdue tasks"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        helperText={duplicateName ? "A saved report with this name already exists — saving will replace it." : "Choose a name you will recognise later."}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSaveOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={!name.trim()}>
                        {duplicateName ? "Replace report" : "Save report"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!pendingDelete} onClose={() => setPendingDelete(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Delete this saved report?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        "{pendingDelete?.name}" will be removed from your saved reports. The report data itself is not affected — you can always save the view again.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingDelete(null)}>Keep it</Button>
                    <Button variant="contained" color="error" onClick={handleDelete}>
                        Delete report
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default MasterReportPresets;
